/**
 * QueueFilters — the Queue tab's view switcher, built on the shared
 * SegmentedPills control: Segment pills (Queue / Live / Eliminated, each
 * icon + count, SELECTED = solid tone fill) plus a History pill (per-run
 * rotation log), and a Layer group that hides in the History view (layers
 * don't apply there). Pure controlled UI.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, type ReactNode } from 'react';
import { History } from 'lucide-react';

import { SearchInput, SegmentedPills, type SegmentedPillTone } from '@/components/ui';

import { layerFilterIcon, stateIcon } from './queue-icons';
import { queueStatusMeta, type QueueBadgeTone } from './queue-status.util';
import { QUEUE_STATUSES, type QueueStatusFilter } from './status';
import {
  isLayerComingSoon,
  QUEUE_LAYERS,
  QUEUE_SEGMENTS,
  type QueueLayerFilter,
  type QueueSegment,
} from './queue-filters';

/** What the lower panel shows: one element segment, or the rotation history. */
export type QueueView = QueueSegment | 'history';

const SEGMENT_TONE: Record<QueueBadgeTone, SegmentedPillTone> = {
  warning: 'warning',
  success: 'success',
  secondary: 'contrast',
};

export interface QueueFiltersProps {
  view: QueueView;
  onView: (view: QueueView) => void;
  layer: QueueLayerFilter;
  onLayer: (l: QueueLayerFilter) => void;
  counts: Record<QueueSegment, number>;
  historyCount?: number;
  /** Live only — active vs disabled. Absent ⇒ the pills are not rendered. */
  status?: QueueStatusFilter;
  onStatus?: (s: QueueStatusFilter) => void;
  /** Row count per status, so a pill can never disagree with the table. */
  statusCounts?: Record<QueueStatusFilter, number>;
  /** Action rendered inline beside the status pills (move-disabled). */
  statusAction?: ReactNode;
  /** Free-text filter over the listed rows (or the runs, in History). */
  search: string;
  onSearch: (next: string) => void;
}

/** What the box says it will match, per view. */
function searchPlaceholder(view: QueueView): string {
  return view === 'history' ? 'Search runs by element…' : 'Name, tracker id, rule…';
}

const QueueFilters: FC<QueueFiltersProps> = ({
  view,
  onView,
  layer,
  onLayer,
  counts,
  historyCount,
  status,
  onStatus,
  statusCounts,
  statusAction,
  search,
  onSearch,
}) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
    <SegmentedPills<QueueView>
      label="View"
      options={[
        ...QUEUE_SEGMENTS.map((s) => ({
          value: s.value as QueueView,
          label: s.label,
          icon: stateIcon(s.value),
          count: counts[s.value],
          tone: SEGMENT_TONE[queueStatusMeta(s.value).tone],
        })),
        {
          value: 'history' as QueueView,
          label: 'History',
          icon: History,
          count: historyCount,
          tone: 'primary' as SegmentedPillTone,
        },
      ]}
      value={view}
      onChange={onView}
    />
    {view !== 'history' ? (
      <SegmentedPills<QueueLayerFilter>
        label="Layer"
        options={QUEUE_LAYERS.map((l) => ({
          value: l.value,
          // Landers/offers are gated for the paths-focused launch — flag "soon".
          label: isLayerComingSoon(l.value) ? `${l.label} · soon` : l.label,
          icon: layerFilterIcon(l.value),
          tone: 'primary' as SegmentedPillTone,
        }))}
        value={layer}
        onChange={onLayer}
      />
    ) : null}
    {/* Live only: the other segments are not running, so active vs disabled
        has nothing to say about them. */}
    {status && onStatus ? (
      <span className="inline-flex items-center gap-1">
        <SegmentedPills<QueueStatusFilter>
          label="Status"
          options={QUEUE_STATUSES.map((s) => ({
            value: s.value,
            label: s.label,
            count: statusCounts?.[s.value],
            tone: (s.value === 'disabled' ? 'warning' : 'primary') as SegmentedPillTone,
          }))}
          value={status}
          onChange={onStatus}
        />
        {/* The verb for the Disabled pill it sits next to — not its own row. */}
        {statusAction}
      </span>
    ) : null}
    {/* A labelled group like VIEW and LAYER, not a right-floated box. `ml-auto`
        pushed it to the far edge of whatever line it wrapped onto, which read
        as an unrelated control and cost a whole extra row. */}
    <div className="flex items-center gap-1.5">
      <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
        Find
      </span>
      {/* The shared SearchInput (added alongside this work for the bank filter
          bars). Sized down to the pill row's 24px and given a fixed width, so
          the filter row reads as one band rather than a staggered puzzle. */}
      <SearchInput
        value={search}
        onValueChange={onSearch}
        placeholder={searchPlaceholder(view)}
        aria-label="Search the queue"
        containerClassName="w-64 flex-none"
        className="h-6 pl-8 pr-8 text-xs"
      />
    </div>
  </div>
);

export default QueueFilters;
